// We need this rule because backend uses snake cases in some cases
import { colorValue } from '@hai/ui-react';
import { TFunction } from 'i18next';
import { isEmpty, pick, pickBy, without } from 'ramda';
import { StreamRequest } from 'src/js/model/api/request/stream-request';
import { ValidateHostnameRequest } from 'src/js/model/api/request/validate-hostname-request';
import { IconStatusType } from 'src/js/model/icon-status-type';
import { constant } from 'src/js/util/constant';
import { isNilOrEmpty } from 'src/js/util/global-util';
import { isEmptyIp } from 'src/js/validator/ip-validator-helper';

import { VideoEncoderState, VideoEncoderViewModel } from '../video-encoder/video-encoder-models';
import { translateStreamEncapsulation, translateStreamState } from './stream-helper';
import {
  FecRTP,
  MetadataEncapsulation,
  MpegProFecLevel,
  SRTAuthentication,
  SRTMode,
  SrtPathRedundancyMode,
  StreamEncapsulation,
  StreamResponse,
  StreamShaping,
  StreamState,
  StreamViewModel,
  getDestinationShorthand,
} from './stream-models';

export const streamStateToIcon = (streamState: StreamState): string => {
  switch (streamState) {
    case StreamState.RUNNING:
      return 'StatusStreaming';
    case StreamState.LISTENING:
      return 'StatusListening';
    case StreamState.CONNECTING:
    case StreamState.CONNECTED:
      return 'StatusConnecting';
    case StreamState.SECURING:
      return 'StatusSecuring';
    case StreamState.PAUSED:
      return 'StatusPaused';
    case StreamState.PUBLISHING:
    case StreamState.RESOLVING:
      return 'StatusResolving';
    case StreamState.SCRAMBLED:
      return 'StatusScrambled';
    case StreamState.FAILED:
      return 'StatusError';
    case StreamState.STOPPED:
      return 'StatusStopped';
    default:
      return 'StreamFailed';
  }
};

// if editing here, also adjust streamStateToSortable()
export const streamStateToColor = (state: StreamState): string => {
  switch (state) {
    case StreamState.RUNNING:
      return colorValue('haiui-green-01');
    case StreamState.STOPPED:
      return colorValue('haiui-gray-07');
    case StreamState.LISTENING:
      return colorValue('haiui-blue-04');
    case StreamState.CONNECTING:
    case StreamState.CONNECTED:
    case StreamState.SECURING:
    case StreamState.PAUSED:
    case StreamState.PUBLISHING:
    case StreamState.RESOLVING:
    case StreamState.SCRAMBLED:
      return colorValue('haiui-amber-01');
    case StreamState.FAILED:
      return colorValue('haiui-red-01');
    default:
      return colorValue('haiui-red-01');
  }
};

export const streamStateToSortable = (state: StreamState): number => {
  switch (state) {
    case StreamState.RUNNING:
      return 1;
    case StreamState.STOPPED:
      return 4;
    case StreamState.LISTENING:
      return 2;
    case StreamState.CONNECTING:
    case StreamState.CONNECTED:
    case StreamState.SECURING:
    case StreamState.PAUSED:
    case StreamState.PUBLISHING:
    case StreamState.RESOLVING:
    case StreamState.SCRAMBLED:
      return 3;
    case StreamState.FAILED:
      return 5;
    default:
      return 5;
  }
};

export const streamStateMessage = (
  model: StreamViewModel,
  videoEncoder: VideoEncoderViewModel,
  t: TFunction,
): string => {
  let streamMsg = model?.stateString;
  if (model?.state === StreamState.RUNNING) {
    if (videoEncoder && videoEncoder?.stats?.state === VideoEncoderState.AWAIT_FRAMING) {
      streamMsg = t('videnc.videoNoInput');
    } else if (videoEncoder && videoEncoder?.stats?.state !== VideoEncoderState.WORKING) {
      streamMsg = t('videnc.videoStopped');
    }
  }
  return streamMsg;
};

export const iconStatusTypeToPanelColor = (statusType: IconStatusType): string | undefined => {
  switch (statusType) {
    case IconStatusType.OK:
    case IconStatusType.ACTIVE:
      return 'green-01';
    case IconStatusType.STREAMING:
      return 'purple';
    case IconStatusType.READY:
      return 'blue-01';
    case IconStatusType.ERROR:
      return 'red-01';
    case IconStatusType.STOPPED:
      return 'gray';
    case IconStatusType.UNASSIGNED:
      return 'gray';
    case IconStatusType.WARNING:
      return 'yellow';
  }
  return undefined;
};

// For ActionBar status filter
export const streamStateToStatus = (state: StreamState): string => {
  switch (state) {
    case StreamState.RUNNING:
    case StreamState.CONNECTED:
      return 'STREAMING';
    case StreamState.LISTENING:
      return 'LISTENING';
    case StreamState.PAUSED:
    case StreamState.PUBLISHING:
    case StreamState.RESOLVING:
    case StreamState.SCRAMBLED:
    case StreamState.FAILED:
    case StreamState.CONNECTING:
    case StreamState.SECURING:
      return 'ALERT';
    case StreamState.STOPPED:
      return 'INACTIVE';
  }
};

export const streamModelToStatus = (model: StreamViewModel): string => {
  return streamStateToStatus(model.state);
};

export const streamListStreamName = (t: TFunction, name?: string): string =>
  isNilOrEmpty(name) ? t('stream.unnamedStream') : name;

// All the fields to be selected for the request based on the selection
const commonFields = [
  'id',
  'userData',
  'encapsulation',
  'name',
  'port',
  'shaping',
  'passphrase',
  'audio',
  'video',
  'metadata',
  'scte',
  'pmtPid',
  'pcrPid',
  'tsStreamId',
  'programNumber',
  'mtu',
  'tos',
  'ttl',
  'overheadPercentage',
  'metadataEncapsulation',
  'metadataBandwidth',
  'networkInterface',
  'sap',
];
const udpRTPUnicastFields = ['fecRtp'];
const udpRTPMulticastFields = ['address', 'sourceIp'];
const srtCommonFields = [
  'adaptive',
  'latency',
  'srtMode',
  'encKeyLength',
  'authentication',
  'passphrase',
  'srtAccessPublishingID',
];
const fecFields = ['level', 'rows', 'columns', 'blockAligned'];
const srtCallerRendezVousFields = ['sourcePort', 'address'];
const srtCallerRedundantFields = ['srtRedundancyPath2Address'];
const srtListenerFields = ['srtListenerSecondPort'];
const srtPathRedundancyFields = [
  'srtRedundancyMode',
  'srtRedundancyPath1Role',
  'srtRedundancyPath2Address',
  'srtRedundancyPath2Port',
  'srtRedundancyPath2SourcePort',
  'srtRedundancyPath1Name',
  'srtRedundancyPath2Name',
  'srtRedundancyPath2NetworkInterface',
];
const srtPathRedundancyOffFields = ['srtRedundancyMode'];

export const createNewStreamViewModel = (): StreamViewModel => ({
  id: constant.newModelId,
  adaptive: false,
  name: '',
  address: '',
  sourceIp: '',
  encapsulation: StreamEncapsulation.UDP,
  metadataEncapsulation: MetadataEncapsulation.SYNC,
  encapsulationTextual: 'TS over UDP',
  videoOutputResolution: '',
  videoBitrate: '',
  audioBitrate: '',
  fecRtp: FecRTP.DISABLED,
  srtMode: SRTMode.CALLER,
  port: undefined,
  latency: 250,
  idleCells: false,
  delayedAudio: false,
  persistent: true,
  mtu: 1496,
  tos: '0x80',
  ttl: 64,
  level: MpegProFecLevel.ROW_AND_COLUMNS,
  columns: 10,
  rows: 5,
  blockAligned: true,
  encrypted: false,
  audioPids: '',
  videoPids: '',
  metadataPids: '',
  sctePids: '',
  statusIcon: 'StatusOK',
  stateString: 'RUNNING',
  networkInterface: 'auto',
  overheadPercentage: 15,
  srtRedundancyMode: SrtPathRedundancyMode.NONE,
  srtRedundancyPath1Role: 'primary',
  srtRedundancyPath2NetworkInterface: 'auto',
  srtRedundancyPath2Address: '',
  srtRedundancyPath2Port: '',
  authentication: SRTAuthentication.NONE,
  encKeyLength: 0,
  passphrase: '',
  passphraseSet: false,
  shaping: StreamShaping.VBR,
  tsStreamId: 1,
  sap: { name: '', advertise: false },
  stats: {},
  audio: [{ id: 0 }],
  video: [{ id: 0 }],
  metadata: [],
  scte: [],
  previewIntervalSec: 10,
  destination: '',
  srtStreamId: false,
  pmtPidAutoAssigned: false,
  pcrPidAutoAssigned: false,
  programNumberAutoAssigned: false,

  state: 0,
  stateSortable: 0,
  selected: false,
  hidden: false,
  updated: new Date().toISOString(),
});

const buildPids = (
  stream: StreamResponse,
  prop: 'audio' | 'video' | 'metadata' | 'scte',
  showEmptyIfAnyAutoassigned = false,
) => {
  let myPids = '';
  let anyAutoAssigned;
  if (stream.info.id !== -1 && stream.info[prop]?.length) {
    if (prop === 'metadata' || prop === 'scte') {
      if (showEmptyIfAnyAutoassigned && stream.info[prop][0].autoassigned) {
        return '';
      }
      return stream.info[prop][0].pid.toString(); // only show first one
    }
    stream.info[prop].map((item) => {
      if (item && item.pid) {
        myPids += item.pid;
        myPids += ', ';
        if (item.autoassigned) {
          anyAutoAssigned = true;
        }
      }
    });
    myPids = myPids.replace(/,\s*$/, '');
  }
  if (showEmptyIfAnyAutoassigned && anyAutoAssigned) {
    return '';
  }
  return myPids;
};

const buildPidPlaceholder = (stream: StreamResponse, prop: 'audio' | 'video' | 'metadata' | 'scte', t: TFunction) => {
  let anyAutoAssigned = false;
  if (stream.info.id !== -1 && stream.info[prop]?.length) {
    stream.info[prop].map((item) => {
      if (item && item.autoassigned) {
        anyAutoAssigned = true;
      }
    });
  }
  if (anyAutoAssigned) {
    return buildPids(stream, prop);
  }
  return t('stream.pidAutoAssigned');
};

const buildPcrPidPlaceholder = (stream: StreamResponse, t: TFunction) => {
  if (stream.info.pcrPidAutoAssigned) {
    return stream.info.pcrPid?.toString();
  }
  return t('stream.pidAutoAssigned');
};
const buildPmtPidPlaceholder = (stream: StreamResponse, t: TFunction) => {
  if (stream.info.pmtPidAutoAssigned) {
    return stream.info.pmtPid?.toString();
  }
  return t('stream.pidAutoAssigned');
};
const buildProgramNumberPlaceholder = (stream: StreamResponse, t: TFunction) => {
  if (stream.info.programNumberAutoAssigned) {
    return stream.info.programNumber?.toString();
  }
  return t('stream.pidAutoAssigned');
};

export const mapStreamViewModel = (stream: StreamResponse, t: TFunction): StreamViewModel => {
  const viewModel: StreamViewModel = {
    name: stream.info.name ?? '',
    id: stream.info.id,
    userData: stream.info.userData,
    statusIcon: streamStateToIcon(stream.stats?.state),
    stateString: translateStreamState(stream.stats.state, t),
    encapsulationTextual: translateStreamEncapsulation(stream.info.encapsulation, t),
    videoOutputResolution: '',
    videoBitrate: '',
    audioBitrate: '',
    metadataEncapsulation: stream.info.metadataEncapsulation,

    // Form parameters
    address: isEmptyIp(stream.info.address) ? '' : stream.info.address,
    sourceIp: isEmptyIp(stream.info.sourceIp) ? '' : stream.info.sourceIp,
    encapsulation: stream.info.encapsulation,
    fecRtp: stream.info.fecRtp ?? FecRTP.DISABLED,
    srtMode: stream.info.srtMode ?? SRTMode.CALLER,
    sourcePort: stream.info.sourcePort === 0 || stream.info.sourcePort === undefined ? '' : stream.info.sourcePort,
    shaping: stream.info.shaping,
    port: stream.info.port,
    latency: stream.info.latency ?? 250,
    mtu: stream.info.mtu,
    tos: stream.info.tos,
    ttl: stream.info.ttl,
    sap: stream.info.sap,
    idleCells: stream.info.idleCells,
    delayedAudio: stream.info.delayedAudio,
    persistent: stream.info.persistent,
    passphrase: '',
    passphraseSet: stream.info.passphraseSet,
    encrypted: stream.info.passphraseSet ?? false,
    audio: stream.info.audio,
    video: stream.info.video,
    metadata: stream.info.metadata,
    scte: stream.info.scte,
    stats: stream.stats,
    previewIntervalSec: 10,
    destination: '',
    bandwithEstimate: `${stream.info.bandwithEstimate} kbps`,
    metadataBandwidth: stream.info.metadataBandwidth,
    metadataBandwidthAutoAssigned: stream.info.metadataBandwidthAutoAssigned,
    metadataBandwidthPlaceholder: t('general.automatic'),
    overheadPercentage: stream.info.overheadPercentage,
    level: stream.info.level !== undefined ? stream.info.level : MpegProFecLevel.ROW_AND_COLUMNS,
    rows: stream.info.rows !== undefined ? stream.info.rows : 5,
    columns: stream.info.columns !== undefined ? stream.info.columns : 10,
    blockAligned: stream.info.blockAligned !== undefined ? stream.info.blockAligned : true,
    // pids
    audioPids: buildPids(stream, 'audio', true),
    videoPids: buildPids(stream, 'video', true),
    metadataPids: buildPids(stream, 'metadata', true),
    sctePids: buildPids(stream, 'scte', true),
    audioPidsPlaceholder: buildPidPlaceholder(stream, 'audio', t),
    videoPidsPlaceholder: buildPidPlaceholder(stream, 'video', t),
    metadataPidsPlaceholder: buildPidPlaceholder(stream, 'metadata', t),
    sctePidsPlaceholder: buildPidPlaceholder(stream, 'scte', t),
    pcrPid: stream.info.pcrPidAutoAssigned ? undefined : stream.info.pcrPid,
    pmtPid: stream.info.pmtPidAutoAssigned ? undefined : stream.info.pmtPid,
    tsStreamId: stream.info.tsStreamId,
    programNumber: stream.info.programNumberAutoAssigned ? undefined : stream.info.programNumber,
    pcrPidPlaceholder: buildPcrPidPlaceholder(stream, t),
    pmtPidPlaceholder: buildPmtPidPlaceholder(stream, t),
    tsStreamIdPlaceholder: stream.info.tsStreamId?.toString(),
    programNumberPlaceholder: buildProgramNumberPlaceholder(stream, t),
    pmtPidAutoAssigned: stream.info.pmtPidAutoAssigned,
    pcrPidAutoAssigned: stream.info.pcrPidAutoAssigned,
    programNumberAutoAssigned: stream.info.programNumberAutoAssigned,

    // stream id
    srtStreamId: stream.info.srtAccessPublishingID !== undefined && stream.info.srtAccessPublishingID.length > 0,
    srtAccessControlPublishingIdType: stream.info.srtAccessControlPublishingIdType,
    srtAccessPublishingID: stream.info.srtAccessPublishingID,
    srtAccessResourceName: stream.info.srtAccessResourceName,
    srtAccessUserName: stream.info.srtAccessUserName,

    // srt
    adaptive: stream.info.adaptive,
    authentication: stream.info.authentication,
    encKeyLength: stream.info.encKeyLength ?? 0,
    networkInterface:
      stream.info.networkInterface && stream.info.networkInterface.length > 0 ? stream.info.networkInterface : 'auto',
    srtRedundancyMode: stream.info.srtRedundancyMode ?? SrtPathRedundancyMode.NONE,
    srtRedundancyPath1Role: stream.info.srtRedundancyPath1Role ?? 'primary',
    srtRedundancyPath1Address: stream.info.srtRedundancyPath1Address ?? '',
    srtRedundancyPath2Address:
      stream.info.srtRedundancyPath2Address === undefined ||
      stream.info.srtRedundancyPath2Address === 'Any' ||
      stream.info.srtRedundancyPath2Address === '0.0.0.0'
        ? ''
        : stream.info.srtRedundancyPath2Address,
    srtRedundancyPath1Port:
      stream.info.srtRedundancyPath1Port === 0 || stream.info.srtRedundancyPath1Port === undefined
        ? ''
        : stream.info.srtRedundancyPath1Port,
    srtRedundancyPath2Port:
      stream.info.srtRedundancyPath2Port === 0 || stream.info.srtRedundancyPath2Port === undefined
        ? ''
        : stream.info.srtRedundancyPath2Port,
    srtRedundancyPath1SourcePort:
      stream.info.srtRedundancyPath1SourcePort === 0 || stream.info.srtRedundancyPath1SourcePort === undefined
        ? ''
        : stream.info.srtRedundancyPath1SourcePort,
    srtRedundancyPath2SourcePort:
      stream.info.srtRedundancyPath2SourcePort === 0 || stream.info.srtRedundancyPath2SourcePort === undefined
        ? ''
        : stream.info.srtRedundancyPath2SourcePort,
    srtRedundancyPath1Name: stream.info.srtRedundancyPath1Name ?? '',
    srtRedundancyPath2Name: stream.info.srtRedundancyPath2Name ?? '',
    srtRedundancyPath2NetworkInterface:
      stream.info.srtRedundancyPath2NetworkInterface && stream.info.srtRedundancyPath2NetworkInterface.length > 0
        ? stream.info.srtRedundancyPath2NetworkInterface
        : 'auto',
    srtListenerSecondPort:
      stream.info.srtListenerSecondPort === 0 || stream.info.srtListenerSecondPort === undefined
        ? ''
        : stream.info.srtListenerSecondPort,

    stateSortable: streamStateToSortable(stream.stats.state),
    state: stream.stats?.state,
    hidden: false,
    selected: false,
    updated: new Date().toISOString(),
  };
  viewModel.destination = getDestinationShorthand(viewModel);

  viewModel.metadataBandwidthPlaceholder = t('general.automatic');
  if (viewModel.metadataBandwidthAutoAssigned && viewModel.metadataBandwidth !== '') {
    viewModel.metadataBandwidthPlaceholder = viewModel.metadataBandwidth.toString();
    // if user switched from VBR to another type, then ignore the value
    viewModel.metadataBandwidth = '';
    if (viewModel.shaping === StreamShaping.VBR) {
      viewModel.metadataBandwidthPlaceholder = t('general.automatic');
    }
  }

  if (viewModel.sap === undefined) {
    viewModel.sap = {};
  }
  if (viewModel.sap.advertise === undefined) {
    viewModel.sap.advertise = false;
  }
  if (viewModel.sap.name === undefined) {
    viewModel.sap.name = '';
  }

  if (
    viewModel.sap.address &&
    viewModel.sap.addressConfigured != null &&
    viewModel.sap.address.length > 0 &&
    viewModel.sap.addressConfigured.length === 0
  ) {
    viewModel.sapAddressPlaceholder = viewModel.sap.address;
    viewModel.sap.address = ''; // the value was auto-assigned and not configured by the user
  }

  // srt addons
  if (viewModel.stats.srt) {
    if (
      viewModel.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE ||
      viewModel.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_BACKUP ||
      viewModel.stats.srt.detectedRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE ||
      viewModel.stats.srt.detectedRedundancyMode === SrtPathRedundancyMode.ACTIVE_BACKUP
    ) {
      viewModel.stats.srt.pathName =
        viewModel.srtRedundancyPath1Name && viewModel.srtRedundancyPath1Name.length > 0
          ? viewModel.srtRedundancyPath1Name
          : t('statistics.path1');
      viewModel.stats.srt.path2.pathName =
        viewModel.srtRedundancyPath2Name && viewModel.srtRedundancyPath2Name.length > 0
          ? viewModel.srtRedundancyPath2Name
          : t('statistics.path2');
    }

    viewModel.stats.srt.peerDecryption = viewModel.stats.srt.decryptError
      ? t('statistics.stream.rows.peerDecryptionInactive', {
          reason: viewModel.stats.srt.decryptError,
        })
      : t('statistics.stream.rows.peerDecryptionActive');
    if (viewModel.stats.srt.path2) {
      viewModel.stats.srt.path2.peerDecryption = viewModel.stats.srt.path2.decryptError
        ? t('statistics.stream.rows.peerDecryptionInactive', {
            reason: viewModel.stats.srt.path2.decryptError,
          })
        : t('statistics.stream.rows.peerDecryptionActive');
    }

    // TODO verify if MX4D needs this
    if (viewModel.stats.srt.rtt >= 1000000) {
      viewModel.stats.srt.rrtWithUnits = viewModel.stats.srt.rtt / 1000000 + t('statistics.units.sec');
    } else if (viewModel.stats.srt.rtt >= 1000) {
      viewModel.stats.srt.rrtWithUnits = viewModel.stats.srt.rtt / 1000 + t('statistics.units.ms');
    } else if (viewModel.stats.srt.rtt > 0) {
      viewModel.stats.srt.rrtWithUnits = t('statistics.units.smallerThan1Ms');
    } else {
      viewModel.stats.srt.rrtWithUnits = undefined;
    }

    if (viewModel.srtRedundancyMode !== SrtPathRedundancyMode.NONE && viewModel.stats.srt.path2) {
      if (viewModel.stats.srt.path2.rtt >= 1000000) {
        viewModel.stats.srt.path2.rrtWithUnits = viewModel.stats.srt.path2.rtt / 1000000 + t('statistics.units.sec');
      } else if (viewModel.stats.srt.path2.rtt >= 1000) {
        viewModel.stats.srt.path2.rrtWithUnits = viewModel.stats.srt.path2.rtt / 1000 + t('statistics.units.ms');
      } else if (viewModel.stats.srt.path2.rtt > 0) {
        viewModel.stats.srt.path2.rrtWithUnits = t('statistics.units.smallerThan1Ms');
      } else {
        viewModel.stats.srt.path2.rrtWithUnits = undefined;
      }
    }
  }

  // console.log('stream viewModel mapped', viewModel);
  return viewModel;
};

const getPidsArray = (pids: string | number) => {
  if (typeof pids === 'string') {
    return pids.length > 0 ? pids.trim().split(',') : [];
  }
  return [pids];
};

export const mapStreamRequest = (streamViewModel: StreamViewModel): StreamRequest => {
  // We have to do the isNan logic here because we convert string to number using Number()
  // If you pass an invalid value to Number(), it will return NaN, which is not trimmed automatically.

  // slice in the audio/video pids
  const videoPids = getPidsArray(streamViewModel.videoPids);
  const audioPids = getPidsArray(streamViewModel.audioPids);
  const metadataPids = getPidsArray(streamViewModel.metadataPids);
  const sctePids = getPidsArray(streamViewModel.sctePids);

  streamViewModel.video.forEach((item, index) => {
    if (videoPids.length > index) {
      item.pid = Number(videoPids[index]);
    } else {
      delete item.pid;
    }
  });
  streamViewModel.audio.forEach((item, index) => {
    if (audioPids.length > index) {
      item.pid = Number(audioPids[index]);
    } else {
      delete item.pid;
    }
  });
  streamViewModel.metadata.forEach((item, index) => {
    if (metadataPids.length > index) {
      item.pid = Number(metadataPids[index]);
    } else {
      delete item.pid;
    }
  });
  streamViewModel.scte.forEach((item, index) => {
    if (sctePids.length > index) {
      item.pid = Number(sctePids[index]);
    } else {
      delete item.pid;
    }
  });

  const cleanedRequest: any = pickBy((value) => typeof value !== 'number' || !isNaN(value), {
    id: streamViewModel.id === constant.newModelId ? undefined : streamViewModel.id,
    userData: streamViewModel.userData,
    name: streamViewModel.name,
    encapsulation: streamViewModel.encapsulation,
    address: streamViewModel.address,
    latency: Number(streamViewModel.latency),
    port: Number(streamViewModel.port),
    shaping: streamViewModel.shaping,
    sourceIp: streamViewModel.sourceIp,
    audio: streamViewModel.audio,
    video: streamViewModel.video,
    metadata: streamViewModel.metadata,
    scte: streamViewModel.scte,
    mtu: streamViewModel.mtu,
    tos: streamViewModel.tos,
    ttl: streamViewModel.ttl,
    overheadPercentage: streamViewModel.overheadPercentage,
    metadataEncapsulation: streamViewModel.metadataEncapsulation,
    metadataBandwidth: streamViewModel.metadataBandwidth,
    sap: streamViewModel.sap,
    networkInterface: streamViewModel.networkInterface,

    pcrPid: streamViewModel.pcrPid,
    pmtPid: streamViewModel.pmtPid,
    tsStreamId: streamViewModel.tsStreamId,
    programNumber: streamViewModel.programNumber,

    fecRtp: streamViewModel.fecRtp,
    level: streamViewModel.level,
    rows: streamViewModel.rows,
    columns: streamViewModel.columns,
    blockAligned: streamViewModel.blockAligned,
    srtMode: streamViewModel.srtMode,
    sourcePort: Number(streamViewModel.sourcePort),
    encKeyLength: streamViewModel.encKeyLength,
    passphrase: streamViewModel.encKeyLength ? streamViewModel.passphrase : '',
    authentication: streamViewModel.authentication,

    adaptive: streamViewModel.adaptive ?? false,
    srtAccessPublishingID: streamViewModel.srtStreamId ? streamViewModel.srtAccessPublishingID : '',

    srtRedundancyMode: streamViewModel.srtRedundancyMode,
    srtRedundancyPath1Role: streamViewModel.srtRedundancyPath1Role,
    srtRedundancyPath2Address: streamViewModel.srtRedundancyPath2Address,
    srtRedundancyPath2Port: streamViewModel.srtRedundancyPath2Port,
    srtRedundancyPath2SourcePort: streamViewModel.srtRedundancyPath2SourcePort,
    srtRedundancyPath1SourcePort: streamViewModel.srtRedundancyPath1SourcePort,
    srtRedundancyPath1Name: streamViewModel.srtRedundancyPath1Name,
    srtRedundancyPath2Name: streamViewModel.srtRedundancyPath2Name,
    srtRedundancyPath2NetworkInterface: streamViewModel.srtRedundancyPath2NetworkInterface,
    srtListenerSecondPort: streamViewModel.srtListenerSecondPort,
  });

  // We have to cleanup the request based on what the user has chosen
  let fieldsToPick: string[] = [];

  switch (+streamViewModel.encapsulation) {
    case StreamEncapsulation.RTP:
    case StreamEncapsulation.UDP:
      if (+streamViewModel.encapsulation === StreamEncapsulation.RTP && streamViewModel.fecRtp !== FecRTP.DISABLED) {
        fieldsToPick = fieldsToPick.concat(fecFields);
      }
      fieldsToPick = fieldsToPick.concat(udpRTPUnicastFields);
      fieldsToPick = fieldsToPick.concat(udpRTPMulticastFields);
      break;
    case StreamEncapsulation.SRT:
      fieldsToPick = fieldsToPick.concat(srtCommonFields);
      if (+streamViewModel.srtMode === SRTMode.LISTENER) {
        fieldsToPick = fieldsToPick.concat(srtListenerFields);
      }
      if (+streamViewModel.srtMode === SRTMode.RENDEZVOUS || +streamViewModel.srtMode === SRTMode.CALLER) {
        fieldsToPick = fieldsToPick.concat(srtCallerRendezVousFields);
      }
      if (
        +streamViewModel.srtMode === SRTMode.CALLER &&
        (streamViewModel.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE ||
          streamViewModel.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_BACKUP)
      ) {
        fieldsToPick = fieldsToPick.concat(srtCallerRedundantFields);
      }

      if (
        (+streamViewModel.srtMode === SRTMode.RENDEZVOUS || +streamViewModel.srtMode === SRTMode.CALLER) &&
        (streamViewModel.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE ||
          streamViewModel.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_BACKUP)
      ) {
        fieldsToPick = fieldsToPick.concat(srtPathRedundancyFields);
        cleanedRequest.sourcePort = cleanedRequest.srtRedundancyPath1SourcePort;
      } else {
        fieldsToPick = fieldsToPick.concat(srtPathRedundancyOffFields);
        if (+streamViewModel.srtMode === SRTMode.LISTENER) {
          cleanedRequest.srtRedundancyMode = SrtPathRedundancyMode.OPTIONAL;
        } else {
          cleanedRequest.srtRedundancyMode = SrtPathRedundancyMode.NONE;
        }
      }
  }
  fieldsToPick = fieldsToPick.concat(commonFields);

  // If encrypted is true but passphrase is empty, we need to strip it down because that's
  // how the backend manages whether the passphrase is set or not
  if (streamViewModel.encrypted && isEmpty(streamViewModel.passphrase)) {
    fieldsToPick = without(['passphrase'], fieldsToPick);
  }

  // The cast here is safe since we know all the required fields are in `commonFields`
  const finalRequest = pick(fieldsToPick, cleanedRequest) as StreamRequest;
  return finalRequest;
};

export const mapValidateHostnameRequest = (hostname: string): ValidateHostnameRequest => ({
  hostname: hostname,
});
